<!-- src/views/NewMembersView.vue -->
<!-- 신규회원 관리: 최근 가입 회원의 수강권·출석·연락 상태를 한 표로 본다 -->
<!-- (docs/architecture/admin-member-management-design.md 5절) -->
<template>
  <div class="new-members-page">
    <!-- 상단: 타이틀 + 기간 + 검색 -->
    <div class="head">
      <div class="title-row">
        <h2>신규회원 관리</h2>
        <div class="periods" role="group" aria-label="가입 기간">
          <button
            v-for="d in dayOptions"
            :key="d"
            type="button"
            :class="['period-btn', { active: days === d }]"
            :aria-pressed="days === d"
            @click="setDays(d)"
          >
            {{ d }}일
          </button>
        </div>
      </div>

      <!-- 검색 (Enter) -->
      <div class="search">
        <span class="icon">🔍</span>
        <input
          v-model="searchInput"
          type="text"
          enterkeyhint="search"
          placeholder="이름 또는 전화번호로 검색 (Enter)"
          aria-label="이름 또는 전화번호로 검색"
          @keyup.enter="submitSearch"
        />
        <button
          v-if="searchInput || q"
          type="button"
          class="clear"
          @click="clearSearch"
        >
          지우기
        </button>
      </div>
    </div>

    <!-- 요약 칸: 연락 안 함·부재 칸은 누르면 그 상태로 거른다 -->
    <div class="summary">
      <div class="tile">
        <span class="tile-k">신규</span>
        <span class="tile-v"
          >{{ countText(summary.total) }}<small>명</small></span
        >
      </div>
      <button
        type="button"
        class="tile tile--btn tone-danger"
        :class="{
          active: contact === 'none',
          zero: !countsShown || !summary.contact_none,
        }"
        :aria-pressed="contact === 'none'"
        title="누르면 연락 안 한 회원만 봅니다"
        @click="toggleContact('none')"
      >
        <span class="tile-k">연락 안 함</span>
        <span class="tile-v">{{ countText(summary.contact_none) }}</span>
      </button>
      <button
        type="button"
        class="tile tile--btn tone-warning"
        :class="{
          active: contact === 'retry',
          zero: !countsShown || !summary.contact_retry,
        }"
        :aria-pressed="contact === 'retry'"
        title="누르면 부재로 다시 연락이 필요한 회원만 봅니다"
        @click="toggleContact('retry')"
      >
        <span class="tile-k">부재 · 다시 필요</span>
        <span class="tile-v">{{ countText(summary.contact_retry) }}</span>
      </button>
      <div
        class="tile tone-warning"
        :class="{ zero: !countsShown || !summary.no_attendance }"
      >
        <span class="tile-k">첫 출석 전</span>
        <span class="tile-v">{{ countText(summary.no_attendance) }}</span>
      </div>
      <div
        class="tile tone-muted"
        :class="{ zero: !countsShown || !summary.not_signed_up }"
      >
        <span class="tile-k">앱 가입 전</span>
        <span class="tile-v">{{ countText(summary.not_signed_up) }}</span>
      </div>
    </div>

    <!-- 연락 상태 필터 -->
    <div class="filters" role="group" aria-label="연락 상태">
      <button
        v-for="f in contactFilters"
        :key="f.value"
        type="button"
        :class="['filter-btn', { active: contact === f.value }]"
        :aria-pressed="contact === f.value"
        @click="setContact(f.value)"
      >
        {{ f.label }}
        <span v-if="countsShown" class="count">{{ f.count }}</span>
      </button>
    </div>

    <!-- 결과 영역 -->
    <div class="list">
      <div v-if="showBackgroundError" class="notice">
        최신 목록을 불러오지 못했습니다.
        <button type="button" class="link-btn" @click="load()">
          다시 시도
        </button>
      </div>

      <div
        v-if="showTable"
        class="nm-table"
        :class="{ stale: isStale }"
        role="table"
        aria-label="신규회원 목록"
      >
        <div class="nm-row nm-head" role="row">
          <div class="cell" role="columnheader">이름 · 전화</div>
          <div class="cell" role="columnheader">가입일</div>
          <div class="cell" role="columnheader">수강권</div>
          <div class="cell" role="columnheader">
            출석<span class="h-sub"> · 다음 예약</span>
          </div>
          <div class="cell h-next" role="columnheader">다음 예약</div>
          <div class="cell" role="columnheader">연락</div>
          <div class="cell h-note" role="columnheader">특이사항</div>
          <div class="cell" role="columnheader">
            <span class="sr-only">연락 기록</span>
          </div>
        </div>

        <div
          v-for="m in members"
          :key="m.user_id"
          class="nm-row nm-item"
          role="row"
          tabindex="0"
          @click="openProfile(m, $event)"
          @auxclick="openProfile(m, $event)"
          @mousedown.middle.prevent
          @keydown.enter.self="openProfile(m, $event)"
        >
          <!-- 이름 · 전화 -->
          <div class="cell cell-name" role="cell">
            <div class="primary name-line">
              <span class="name">{{ m.name }}</span>
              <span
                v-if="m.free_trial"
                class="badge tone-brand"
                title="무료체험을 거쳐 가입"
                >체험</span
              >
            </div>
            <div class="secondary">
              {{ prettyPhone(m.phone_number) || "—" }}
            </div>
          </div>

          <!-- 가입일 -->
          <div class="cell cell-join" role="cell">
            <span class="cell-k">가입일</span>
            <div class="primary">{{ dotDate(m.join_date) || "—" }}</div>
            <div class="secondary inline">
              <span v-if="isFiniteNumber(m.days_since_join)"
                >D+{{ m.days_since_join }}</span
              >
              <span
                v-if="!m.app_signed_up"
                class="badge tone-muted"
                title="초대 문자로 앱 가입을 아직 끝내지 않음"
                >앱 가입 전</span
              >
            </div>
          </div>

          <!-- 수강권 -->
          <div class="cell cell-membership" role="cell">
            <span class="cell-k">수강권</span>
            <template v-if="membershipsOf(m).length">
              <div
                v-for="(ms, i) in membershipsOf(m).slice(0, 2)"
                :key="ms.id || i"
                :class="[i === 0 ? 'primary' : 'secondary', 'membership-line']"
                :title="membershipTitle(ms)"
              >
                <span class="ms-type">{{ ms.type || "수강권" }}</span>
                <span v-if="membershipCount(ms)" class="ms-count">{{
                  membershipCount(ms)
                }}</span>
                <span v-if="ms.state === 'upcoming'" class="badge tone-info"
                  >시작 예정 {{ shortDate(ms.start_date) }}</span
                >
              </div>
              <!-- 줄에 못 든 수강권. 시작 예정(재계약)이 있으면 배지로 남긴다 -->
              <div
                v-if="membershipsOf(m).length > 2"
                class="secondary membership-line"
                :title="moreMembershipsTitle(m)"
              >
                <span>외 {{ membershipsOf(m).length - 2 }}개</span>
                <span v-if="hiddenUpcoming(m)" class="badge tone-info"
                  >시작 예정 {{ shortDate(hiddenUpcoming(m).start_date) }}</span
                >
              </div>
            </template>
            <div v-else class="primary dim">수강권 없음</div>
          </div>

          <!-- 출석 -->
          <div class="cell cell-attendance" role="cell">
            <span class="cell-k">출석</span>
            <template v-if="m.attendance_count > 0">
              <div class="primary">{{ m.attendance_count }}회</div>
              <div v-if="m.last_attendance" class="secondary">
                최근 {{ shortDate(m.last_attendance) }}
              </div>
            </template>
            <div v-else class="primary">
              <span class="badge tone-warning">첫 출석 전</span>
            </div>
          </div>

          <!-- 다음 예약 -->
          <div class="cell cell-next" role="cell">
            <span class="cell-k">다음 예약</span>
            <template v-if="m.next_booking">
              <div class="primary">
                {{ shortDate(m.next_booking.date) }} {{ m.next_booking.time }}
              </div>
              <div class="secondary">
                {{ classTypeLabel(m.next_booking.class_type) }}
              </div>
            </template>
            <div v-else class="primary dim">—</div>
          </div>

          <!-- 연락 -->
          <div
            class="cell cell-contact"
            role="cell"
            :title="
              m.contact && m.contact.last_content
                ? m.contact.last_content
                : null
            "
          >
            <span class="cell-k">연락</span>
            <div class="primary">
              <span
                class="badge"
                :class="`tone-${contactStatusTone(contactOf(m).status)}`"
              >
                {{ contactStatusLabel(contactOf(m).status) || "연락 안 함" }}
              </span>
            </div>
            <div v-if="contactOf(m).count > 0" class="secondary">
              {{ contactLine(contactOf(m)) }}
            </div>
            <!-- 카드에서만: 마우스 올림(title)이 없으므로 마지막 내용을 보인다 -->
            <div v-if="contactOf(m).last_content" class="contact-content">
              {{ contactOf(m).last_content }}
            </div>
          </div>

          <!-- 특이사항 -->
          <div class="cell cell-note" role="cell">
            <span class="cell-k">특이사항</span>
            <!-- 앞뒤 공백·빈 줄을 뺀 값(회원 상세·회원관리와 같음). 앞의 빈 줄이 카드의 세 줄을 먹어 둘째 줄이 가려졌다(4차 리뷰) -->
            <div v-if="noteOf(m)" class="primary note-text" :title="noteOf(m)">
              {{ noteOf(m) }}
            </div>
            <div v-else class="primary dim">—</div>
          </div>

          <!-- 연락 기록 버튼: 줄 이동을 막는다 -->
          <div class="cell cell-action" role="cell">
            <button
              type="button"
              class="log-btn"
              @click.stop="openContactLog(m)"
              @auxclick.stop
            >
              연락 기록
            </button>
          </div>
        </div>
      </div>

      <div v-else-if="showLoading" class="state">불러오는 중…</div>

      <div v-else-if="showError" class="state state--error">
        <p>신규회원 목록을 불러오지 못했습니다.</p>
        <button type="button" class="retry-btn" @click="load()">
          다시 시도
        </button>
      </div>

      <div v-else-if="showEmpty" class="state">{{ emptyMessage }}</div>
    </div>

    <ContactLogModal
      v-if="logMember"
      ref="contactLog"
      :member="logMember"
      @close="logMember = null"
      @saved="onContactSaved"
    />
  </div>
</template>

<script>
import ContactLogModal from "@/components/ContactLogModal.vue";
import { classTypeLabel } from "@/lib/classTypes";
import {
  contactResultLabel,
  contactStatusLabel,
  contactStatusTone,
  dotDate,
  fetchNewMembers,
  membershipCountText,
  noteDate,
  prettyPhone,
  shortDate,
} from "@/lib/memberNotes";

const DAY_OPTIONS = [30, 60, 90];
const DEFAULT_DAYS = 30;
const CONTACT_VALUES = ["all", "none", "retry", "done"];
const DEFAULT_CONTACT = "all";

function emptySummary() {
  return {
    total: 0,
    contact_none: 0,
    contact_retry: 0,
    contact_done: 0,
    no_attendance: 0,
    not_signed_up: 0,
  };
}

// 연락 기록이 없을 때 서버가 주는 모양과 같다
const NO_CONTACT = {
  status: "none",
  count: 0,
  last_at: "",
  last_author: "",
  last_result: "",
  last_content: "",
};

// 수강권 칸 순서: 지금 쓰는 수강권(만료 빠른 순) → 시작 예정(시작 빠른 순). 회원 상세 수강권 카드와 같다. API는 시작일 순이라 곧 끝나는
// 수강권이 둘째 줄로 밀리고, 재계약한 시작 예정 수강권은 "외 n개"에 숨었다(4차 리뷰). 같은 값이면 API 순서(sort는 안정 정렬)
function sortMemberships(list) {
  if (!Array.isArray(list)) return [];
  const day = (v) => String(v || "").slice(0, 10);
  const upcoming = (ms) => (ms.state === "upcoming" ? 1 : 0);
  return [...list].sort((a, b) => {
    if (upcoming(a) !== upcoming(b)) return upcoming(a) - upcoming(b);
    return upcoming(a)
      ? day(a.start_date).localeCompare(day(b.start_date))
      : day(a.expired_at).localeCompare(day(b.expired_at));
  });
}

function firstOf(v) {
  return Array.isArray(v) ? v[0] : v;
}

// URL 쿼리(?days=&contact=&q=) → 화면 상태. 잘못된 값은 기본값
function parseQuery(query) {
  const days = Number(firstOf(query.days));
  const contact = firstOf(query.contact);
  const q = firstOf(query.q);
  return {
    days: DAY_OPTIONS.includes(days) ? days : DEFAULT_DAYS,
    contact: CONTACT_VALUES.includes(contact) ? contact : DEFAULT_CONTACT,
    q: typeof q === "string" ? q.trim() : "",
  };
}

// 화면 상태 → URL 쿼리. 기본값은 주소에 쓰지 않는다
function buildQuery({ days, contact, q }) {
  const query = {};
  if (days !== DEFAULT_DAYS) query.days = String(days);
  if (contact !== DEFAULT_CONTACT) query.contact = contact;
  if (q) query.q = q;
  return query;
}

function sameQuery(a, b) {
  const ka = Object.keys(a);
  const kb = Object.keys(b);
  if (ka.length !== kb.length) return false;
  return ka.every((k) => String(firstOf(b[k])) === a[k]);
}

export default {
  name: "NewMembersView",
  components: { ContactLogModal },
  data() {
    return {
      dayOptions: DAY_OPTIONS,
      // 적용된 조건 (URL 쿼리와 같다)
      days: DEFAULT_DAYS,
      contact: DEFAULT_CONTACT,
      q: "",
      // 검색칸에 치는 중인 글자 (Enter로 적용)
      searchInput: "",

      members: [],
      summary: emptySummary(),
      loaded: false,
      loading: false,
      error: false,
      // 지금 화면에 보이는 목록을 읽은 조건
      loadedKey: "",
      requestSeq: 0,

      // [연락 기록] 모달 대상 회원
      logMember: null,
    };
  },
  computed: {
    requestKey() {
      return `${this.days}|${this.contact}|${this.q}`;
    },
    // 보이는 목록이 지금 조건과 다르면(조건을 바꾸고 읽는 중) 흐리게 보인다
    isStale() {
      return this.loading && this.loadedKey !== this.requestKey;
    },
    contactFilters() {
      const s = this.summary;
      return [
        { value: "all", label: "전체", count: s.total },
        { value: "none", label: "연락 안 함", count: s.contact_none },
        { value: "retry", label: "부재", count: s.contact_retry },
        { value: "done", label: "연락함", count: s.contact_done },
      ];
    },
    // 다른 조건의 목록을 읽다 실패함(보이는 옛 목록은 지금 조건의 것이 아니다)
    switchFailed() {
      return this.error && this.loadedKey !== this.requestKey;
    },
    // 그 실패를 보인다. [다시 시도]로 다시 읽는 동안은 "불러오는 중"을 보인다(4차 리뷰: 오류 칸이 그대로라 누른 것이 보이지 않았다)
    showError() {
      return this.switchFailed && !this.loading;
    },
    // 같은 조건을 다시 읽다(창이 다시 보일 때 등) 실패하면 목록은 두고 알림만
    showBackgroundError() {
      return this.error && this.loaded && !this.switchFailed && !this.loading;
    },
    // 요약 칸·필터 숫자를 보일지. 다른 기간·조건을 읽다 실패하면 앞 조건의 숫자를 새로 고른 버튼 옆에 남기지 않는다(3차 리뷰)
    countsShown() {
      return this.loaded && !this.switchFailed;
    },
    showTable() {
      return this.loaded && !this.switchFailed && this.members.length > 0;
    },
    // 처음 읽을 때, 빈 목록에서 조건을 바꿔 읽는 중, 또는 실패한 조건을 다시 읽는 중
    showLoading() {
      return (
        this.loading &&
        (!this.loaded ||
          this.switchFailed ||
          (this.isStale && this.members.length === 0))
      );
    },
    showEmpty() {
      return (
        this.loaded &&
        !this.switchFailed &&
        !this.isStale &&
        this.members.length === 0
      );
    },
    emptyMessage() {
      if (this.contact === DEFAULT_CONTACT && !this.q) {
        return `최근 ${this.days}일 안에 가입한 회원이 없습니다.`;
      }
      return "조건에 맞는 신규회원이 없습니다.";
    },
  },
  watch: {
    // 뒤로 가기·필터 버튼 모두 여기서 읽는다
    "$route.query"() {
      // 다른 화면으로 나가는 중이면 무시
      if (this.$route.name !== "NewMembers") return;
      this.applyRouteQuery();
    },
  },
  created() {
    this.applyRouteQuery();
  },
  // [연락 기록] 모달에 쓰던 내용이 있으면 앱 안에서 다른 화면으로 나가기 전(뒤로 가기·사이드메뉴)에도 모달 닫기와 같이 묻는다.
  // 새로 고침·탭 닫기·주소 입력·앱 첫 화면에서 뒤로 가기는 onBeforeUnload(브라우저 확인 창)가 묻는다(4차 리뷰)
  beforeRouteLeave() {
    const modal = this.$refs.contactLog;
    if (modal && modal.hasDraft() && !modal.confirmDiscard()) return false;
    return true;
  },
  mounted() {
    document.addEventListener("visibilitychange", this.onVisibilityChange);
    window.addEventListener("beforeunload", this.onBeforeUnload);
  },
  beforeUnmount() {
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    window.removeEventListener("beforeunload", this.onBeforeUnload);
  },
  methods: {
    prettyPhone,
    dotDate,
    shortDate,
    // 다음 예약 타입은 값(class_type_enum, 예: other)이 아니라 표시명(기타)으로 보인다. 서버가 type_label을 주지 않아 표로 바꾼다
    classTypeLabel,
    contactStatusLabel,
    contactStatusTone,

    applyRouteQuery() {
      const next = parseQuery(this.$route.query);
      // 검색어가 그대로면 검색칸에 치던 글자는 남긴다
      if (next.q !== this.q || !this.loaded) this.searchInput = next.q;
      this.days = next.days;
      this.contact = next.contact;
      this.q = next.q;
      this.load();
    },

    // 조건을 URL에 담는다. 주소가 바뀌면 watch가 다시 읽는다
    updateQuery(patch) {
      const next = {
        days: this.days,
        contact: this.contact,
        q: this.q,
        ...patch,
      };
      const query = buildQuery(next);
      if (sameQuery(query, this.$route.query)) {
        // 같은 조건이면 주소는 그대로 두고 다시 읽기만 한다
        this.load();
        return;
      }
      this.$router.replace({ query }).catch(() => {});
    },

    async load() {
      const seq = ++this.requestSeq;
      const key = this.requestKey;
      this.loading = true;
      try {
        const res = await fetchNewMembers({
          days: this.days,
          contact: this.contact,
          search: this.q,
        });
        // 늦게 온 옛 응답은 버린다
        if (seq !== this.requestSeq) return;
        this.members = (Array.isArray(res?.members) ? res.members : []).map(
          (m) => ({ ...m, memberships: sortMemberships(m.memberships) })
        );
        this.summary = { ...emptySummary(), ...(res?.summary || {}) };
        this.loadedKey = key;
        this.loaded = true;
        this.error = false;
      } catch (e) {
        if (seq !== this.requestSeq) return;
        console.error("신규회원 목록 불러오기 실패:", e);
        this.error = true;
      } finally {
        if (seq === this.requestSeq) this.loading = false;
      }
    },

    onBeforeUnload(event) {
      const modal = this.$refs.contactLog;
      // 모달의 전화번호(tel:)를 눌러 전화를 거는 것은 떠나는 것이 아니다(5차 리뷰)
      if (!modal || !modal.hasDraft() || modal.isDialing()) return;
      event.preventDefault();
      event.returnValue = "";
    },

    // 다른 코치가 남긴 기록을 반영하려고 창이 다시 보이면 다시 읽는다
    onVisibilityChange() {
      if (document.visibilityState === "visible") this.load();
    },

    // 기간·필터·요약 칸을 누르면 검색칸에 Enter 없이 친 글자도 함께 적용한다(회원관리 목록·main과 같음). 예전에는 적용된 검색어로만
    // 걸러, 검색칸의 이름과 다른 옛 검색어로 거른 목록이 "없음"으로 보였다(3차 리뷰)
    setDays(d) {
      this.updateQuery({ days: d, q: this.searchInput.trim() });
    },
    setContact(value) {
      this.updateQuery({ contact: value, q: this.searchInput.trim() });
    },
    // 요약 칸: 같은 칸을 다시 누르면 전체
    toggleContact(value) {
      this.updateQuery({
        contact: this.contact === value ? DEFAULT_CONTACT : value,
        q: this.searchInput.trim(),
      });
    },
    submitSearch() {
      this.updateQuery({ q: this.searchInput.trim() });
    },
    clearSearch() {
      this.searchInput = "";
      this.updateQuery({ q: "" });
    },

    openProfile(m, event) {
      // 가운데 버튼 말고 다른 보조 버튼(오른쪽 등)은 무시한다
      const middle = event?.type === "auxclick";
      if (middle && event.button !== 1) return;
      // 글자를 끌어 고른 경우(전화번호 복사 등)는 이동하지 않는다
      if (event?.type === "click") {
        const selected = window.getSelection
          ? String(window.getSelection())
          : "";
        if (selected) return;
      }
      const path = `/profile/${encodeURIComponent(m.user_id)}`;
      // Ctrl/⌘ + 클릭과 가운데 클릭은 새 탭. 새 탭에는 방문 기록(history.state.back)이 없으므로 보던 목록 주소를 from에 실어
      // 사이드메뉴가 "신규회원 관리"를 켜고 그 목록으로 돌아가게 한다(3차 리뷰)
      if (middle || (event && (event.ctrlKey || event.metaKey))) {
        const href = this.$router.resolve({
          path,
          query: { from: this.$route.fullPath },
        }).href;
        window.open(href, "_blank", "noopener");
        return;
      }
      this.$router.push(path);
    },

    openContactLog(m) {
      this.logMember = m;
    },
    onContactSaved() {
      this.logMember = null;
      this.load();
    },

    // ---------------- 표시 ----------------

    countText(n) {
      return this.countsShown && Number.isFinite(n) ? String(n) : "–";
    },
    isFiniteNumber(n) {
      return Number.isFinite(n);
    },
    // 읽을 때 sortMemberships로 정렬해 둔 수강권
    membershipsOf(m) {
      return Array.isArray(m.memberships) ? m.memberships : [];
    },
    // 두 줄에 못 든 수강권 가운데 첫 시작 예정
    hiddenUpcoming(m) {
      return (
        this.membershipsOf(m)
          .slice(2)
          .find((ms) => ms.state === "upcoming") || null
      );
    },
    moreMembershipsTitle(m) {
      return this.membershipsOf(m)
        .slice(2)
        .map((ms) => this.membershipTitle(ms))
        .join("\n");
    },
    noteOf(m) {
      return (m.admin_note || "").trim();
    },
    // 남은 횟수. 무제한은 종류 이름에 이미 드러나면 다시 쓰지 않는다(주2회권은 "주 2회", membershipCountText)
    membershipCount(ms) {
      if (ms.total_count === -1 && ms.type !== "주2회권") {
        return /unlimited|무제한/i.test(ms.type || "") ? "" : "무제한";
      }
      return membershipCountText(ms);
    },
    membershipTitle(ms) {
      const range = [dotDate(ms.start_date), dotDate(ms.expired_at)]
        .filter(Boolean)
        .join(" ~ ");
      return [ms.type, membershipCountText(ms), range]
        .filter(Boolean)
        .join(" · ");
    },
    contactOf(m) {
      return m.contact || NO_CONTACT;
    },
    // "10.14 김코치 · 통화"
    contactLine(c) {
      const who = [noteDate(c.last_at), c.last_author]
        .filter(Boolean)
        .join(" ");
      const result = contactResultLabel(c.last_result);
      return [who, result].filter(Boolean).join(" · ");
    },
  },
};
</script>

<style scoped>
.new-members-page {
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: #151718;
  border-radius: 10px;
  padding: 16px;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  color: #fff;
}

/* ---------------- 상단 ---------------- */
.head {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  min-width: 0;
}

.title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}

.head h2 {
  margin: 0;
  font-size: 24px;
  font-weight: 400;
  color: #fff;
}

/* 기간 버튼 (붙어 있는 한 묶음) */
.periods {
  display: inline-flex;
  border: 1px solid #333;
  border-radius: 6px;
  overflow: hidden;
}

.period-btn {
  height: 36px;
  min-width: 64px;
  padding: 0 14px;
  border: 0;
  border-left: 1px solid #333;
  background: #1b1d1e;
  color: #9d9d9d;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
}

.period-btn:first-child {
  border-left: 0;
}

.period-btn:hover:not(.active) {
  background: #252728;
  color: #fff;
}

.period-btn.active {
  background: #78ff47;
  color: #000;
  font-weight: 600;
}

/* 키보드 초점: 묶음의 overflow: hidden이 버튼 바깥 테두리를 잘라 초점이 보이지 않았다(6차 리뷰). 안쪽에 그린다 */
.period-btn:focus-visible {
  outline: 2px solid #fff;
  outline-offset: -2px;
}

.period-btn.active:focus-visible {
  outline-color: #000;
}

/* 검색 */
.search {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
}

.search .icon {
  position: absolute;
  left: 12px;
  pointer-events: none;
}

.search input {
  width: 100%;
  height: 48px;
  background: #151718;
  border-radius: 5px;
  border: 1px solid #333;
  outline: none;
  color: #fff;
  font-size: 15px;
  padding: 0 84px 0 38px;
  min-width: 0;
  box-sizing: border-box;
}

.search input:focus {
  border-color: #78ff47;
}

.search .clear {
  position: absolute;
  right: 8px;
  height: 32px;
  padding: 0 10px;
  border-radius: 5px;
  border: 1px solid #333;
  background: #1b1d1e;
  color: #c8c8c8;
  font-size: 13px;
  cursor: pointer;
}

.search .clear:hover {
  background: #252728;
  color: #fff;
}

/* ---------------- 요약 칸 ---------------- */
.summary {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 10px;
}

/* 요약 칸은 상자 없이 숫자만 보이고, 누르면 거르는 칸(연락 안 함·부재)만 버튼처럼 테두리·배경·›를 둔다.
   예전에는 다섯 칸이 같은 상자라 "첫 출석 전"·"앱 가입 전"도 눌러질 것처럼 보였다(2차 리뷰) */
.tile {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  /* 칸 이름이 두 줄이 되어도 숫자는 다른 칸과 같은 높이(아래)에 둔다 */
  justify-content: space-between;
  gap: 4px;
  min-width: 0;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid transparent;
  background: transparent;
  color: #fff;
  font: inherit;
  text-align: left;
}

/* 칸 이름은 자르지 않는다. 1101~1110px에서 "부재 · 다시 필요"가 "부재 · 다시 ..."로 잘렸다(3차 리뷰). 모자라면 낱말 사이에서 줄을 바꾼다 */
.tile-k {
  font-size: 13px;
  color: #8e8e8e;
  max-width: 100%;
  word-break: keep-all;
  overflow-wrap: anywhere;
}

.tile-v {
  font-size: 24px;
  font-weight: 700;
  line-height: 1.2;
}

.tile-v small {
  margin-left: 2px;
  font-size: 14px;
  font-weight: 400;
  color: #8e8e8e;
}

.tile.tone-danger .tile-v {
  color: #ef4444;
}

.tile.tone-warning .tile-v {
  color: #fbbf24;
}

.tile.tone-muted .tile-v {
  color: #c8c8c8;
}

/* 0이면 색을 빼서 눈에 덜 띄게 */
.tile.zero .tile-v {
  color: #6b7280;
}

.tile--btn {
  position: relative;
  padding-right: 30px;
  border-color: #333;
  background: #1b1d1e;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s;
}

.tile--btn::after {
  content: "›";
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 20px;
  line-height: 1;
  color: #8e8e8e;
}

.tile--btn:hover {
  background: #252728;
  border-color: #4a4c4d;
}

.tile--btn.active {
  border-color: #78ff47;
  background: rgba(120, 255, 71, 0.06);
}

.tile--btn.active .tile-k {
  color: #78ff47;
}

/* ---------------- 연락 상태 필터 ---------------- */
.filters {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.filter-btn {
  height: 40px;
  padding: 0 16px;
  border-radius: 6px;
  background: #1b1d1e;
  color: #9d9d9d;
  border: 1px solid #333;
  cursor: pointer;
  transition: all 0.2s;
}

.filter-btn:hover:not(.active) {
  background: #252728;
}

.filter-btn.active {
  background: #78ff47;
  color: #000;
  border-color: #78ff47;
}

.filter-btn .count {
  margin-left: 6px;
  font-weight: 700;
}

/* ---------------- 목록 ---------------- */
.list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  min-width: 0;
}

.notice {
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid rgba(251, 191, 36, 0.35);
  background: rgba(251, 191, 36, 0.08);
  color: #fbbf24;
  font-size: 14px;
}

.link-btn {
  margin-left: 6px;
  padding: 0;
  border: 0;
  background: none;
  color: #fff;
  font-size: 14px;
  text-decoration: underline;
  cursor: pointer;
}

.state {
  padding: 40px 16px;
  border-radius: 10px;
  border: 1px dashed #2a2a2a;
  text-align: center;
  color: #c8c8c8;
  font-size: 15px;
}

.state p {
  margin: 0 0 12px;
}

.state--error {
  color: #ef4444;
}

.retry-btn {
  height: 38px;
  padding: 0 16px;
  border-radius: 6px;
  border: 1px solid #333;
  background: #1b1d1e;
  color: #fff;
  cursor: pointer;
}

.retry-btn:hover {
  background: #252728;
}

/* 표: 머리글과 줄이 같은 칸 너비를 쓴다 */
.nm-table {
  container: nm-list / inline-size;
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  min-width: 0;
  transition: opacity 0.15s;
}

.nm-table.stale {
  opacity: 0.5;
  pointer-events: none;
}

/* 칸 최소 너비는 줄이면 안 되는 내용 기준: 전화번호 "010-1234-5678", 배지 "시작 예정 10.25",
   "10.16 19:00", "10.14 김코치 · 부재". 특이사항은 말줄임이라 가장 먼저 좁아진다 */
.nm-row {
  display: grid;
  grid-template-columns:
    minmax(104px, 1.1fr) /* 이름 · 전화 */
    minmax(88px, 0.9fr) /* 가입일 */
    minmax(110px, 1.15fr) /* 수강권 */
    minmax(72px, 0.75fr) /* 출석 */
    minmax(88px, 0.85fr) /* 다음 예약 */
    minmax(120px, 1.3fr) /* 연락 */
    minmax(56px, 1.2fr) /* 특이사항 */
    76px; /* 연락 기록 버튼 */
  column-gap: 10px;
  align-items: center;
  padding: 12px;
  box-sizing: border-box;
}

/* 줄과 같은 테두리 두께를 두어 칸 너비를 똑같이 맞춘다 */
.nm-head {
  padding-top: 4px;
  padding-bottom: 4px;
  font-size: 12px;
  color: #8e8e8e;
  border: 1px solid transparent;
  border-bottom-color: #262626;
}

.nm-item {
  background: #0a0a0a;
  border: 1px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}

.nm-item:hover {
  background: #121314;
  border-color: #333;
}

.nm-item:focus-visible {
  outline: 2px solid #78ff47;
  outline-offset: -2px;
}

.cell {
  min-width: 0;
}

/* 카드에서만 보이는 칸 이름 */
.cell-k {
  display: none;
}

.primary {
  font-size: 14px;
  line-height: 1.45;
  color: #fff;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.secondary {
  margin-top: 2px;
  font-size: 13px;
  line-height: 1.4;
  color: #9d9d9d;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.secondary.inline {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  white-space: normal;
}

.dim {
  color: #6b7280;
}

.name-line {
  display: flex;
  align-items: center;
  gap: 6px;
}

.name {
  font-size: 15px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
}

.nm-item:hover .name {
  color: #78ff47;
}

.membership-line {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  column-gap: 6px;
  row-gap: 2px;
  white-space: normal;
}

.ms-type {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

.ms-count {
  color: #c8c8c8;
  white-space: nowrap;
}

.secondary .ms-count {
  color: #9d9d9d;
}

.note-text {
  color: #e5e5e5;
}

/* 표에서는 머리글의 "· 다음 예약"을 숨긴다(좁은 표에서만 보임) */
.h-sub {
  display: none;
}

.contact-content {
  display: none;
}

.cell-action {
  display: flex;
  justify-content: flex-end;
}

.log-btn {
  height: 34px;
  padding: 0 10px;
  border-radius: 6px;
  border: 1px solid #333;
  background: #1b1d1e;
  color: #fff;
  font-size: 13px;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s, color 0.2s;
}

.log-btn:hover {
  border-color: #78ff47;
  color: #78ff47;
  background: #151718;
}

/* 배지 */
.badge {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  height: 20px;
  padding: 0 7px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.06);
  color: #c8c8c8;
}

.badge.tone-danger {
  color: #ef4444;
  background: rgba(239, 68, 68, 0.12);
  border-color: rgba(239, 68, 68, 0.35);
}

.badge.tone-warning {
  color: #fbbf24;
  background: rgba(251, 191, 36, 0.12);
  border-color: rgba(251, 191, 36, 0.35);
}

.badge.tone-safe {
  color: #22c55e;
  background: rgba(34, 197, 94, 0.12);
  border-color: rgba(34, 197, 94, 0.35);
}

.badge.tone-info {
  color: #60a5fa;
  background: rgba(96, 165, 250, 0.12);
  border-color: rgba(96, 165, 250, 0.35);
}

.badge.tone-brand {
  color: #78ff47;
  background: rgba(120, 255, 71, 0.1);
  border-color: rgba(120, 255, 71, 0.35);
}

.badge.tone-muted {
  color: #9ca3af;
  background: rgba(156, 163, 175, 0.1);
  border-color: rgba(156, 163, 175, 0.3);
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* ---------------- 반응형 ---------------- */
@media (max-width: 1280px) {
  .summary {
    gap: 8px;
  }

  .tile {
    padding: 10px 12px;
  }

  .tile--btn {
    padding-right: 22px;
  }

  .tile--btn::after {
    right: 8px;
  }

  .tile-v {
    font-size: 20px;
  }
}

@media (max-width: 720px) {
  .head h2 {
    font-size: 20px;
  }

  .summary {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  /* 신규 n명은 한 줄 전체 */
  .summary .tile:first-child {
    grid-column: 1 / -1;
    flex-direction: row;
    align-items: baseline;
    justify-content: space-between;
  }

  /* 글자 너비만큼 나눠 가져 "연락 안 함 5"가 두 줄로 깨지지 않게 한다(모자라면 다음 줄로) */
  .filter-btn {
    flex: 1 1 auto;
    padding: 0 10px;
    white-space: nowrap;
  }

  .periods {
    width: 100%;
  }

  .period-btn {
    flex: 1;
  }
}

/* 표가 빠듯한 너비(821~960px, 사이드메뉴가 보이는 1280px 안팎 화면): 칸 사이·줄 안쪽 여백을 줄여 칸 최소 너비를 지킨다 */
@container nm-list (max-width: 960px) {
  .nm-row {
    column-gap: 6px;
    padding-left: 8px;
    padding-right: 8px;
  }
}

/*
 * 사이드메뉴가 보이는 1101~1260px 화면처럼 표가 좁아지면(≤ 820px, 화면 > 860px) 카드 대신 두 줄 표로 줄인다:
 * 다음 예약은 출석·연락 칸 아래, 특이사항은 이름·가입일·수강권 칸 아래 둘째 줄(한 줄 말줄임)로 옮긴다.
 * 예전에는 여기서도 카드가 되어 같은 회원 수가 3배 넘게 긴 페이지가 되었다. 표 최소 너비는 약 640px(1101px 화면에서 661px)
 */
@media (min-width: 861px) {
  @container nm-list (max-width: 820px) {
    .nm-row {
      grid-template-columns:
        minmax(104px, 1.1fr) /* 이름 · 전화 */
        minmax(84px, 0.9fr) /* 가입일 */
        minmax(110px, 1.2fr) /* 수강권 */
        minmax(80px, 0.85fr) /* 출석 (+ 다음 예약) */
        minmax(120px, 1.3fr) /* 연락 */
        76px; /* 연락 기록 버튼 */
      column-gap: 6px;
      row-gap: 6px;
      padding-left: 8px;
      padding-right: 8px;
    }

    .h-next,
    .h-note {
      display: none;
    }

    .h-sub {
      display: inline;
    }

    .cell-note {
      grid-row: 2;
      grid-column: 1 / 4;
    }

    .cell-next {
      grid-row: 2;
      grid-column: 4 / 6;
    }

    .cell-note,
    .cell-next {
      display: flex;
      align-items: baseline;
      gap: 6px;
    }

    .cell-note .cell-k,
    .cell-next .cell-k {
      display: block;
      flex-shrink: 0;
      font-size: 12px;
      color: #8e8e8e;
    }

    .cell-note .primary,
    .cell-next .primary {
      min-width: 0;
    }

    .cell-next .secondary {
      margin-top: 0;
      min-width: 0;
    }
  }
}

/*
 * 화면 ≤ 860px(설계 4·5절)이면 줄을 카드로 쌓는다.
 * 사이드메뉴는 1100px 이하에서 숨으므로, 사이드메뉴가 보이는 화면은 위의 두 줄 표가 된다.
 */
@media (max-width: 860px) {
  .nm-head {
    display: none;
  }

  .nm-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 16px;
    row-gap: 12px;
    align-items: start;
    padding: 14px;
  }

  .nm-item {
    border-color: #1f1f1f;
  }

  .cell-k {
    display: block;
    margin-bottom: 2px;
    font-size: 12px;
    color: #8e8e8e;
  }

  .cell-name,
  .cell-contact,
  .cell-note,
  .cell-action {
    grid-column: 1 / -1;
  }

  .cell-name {
    padding-bottom: 10px;
    border-bottom: 1px solid #1f1f1f;
  }

  .name {
    font-size: 17px;
  }

  /* 카드에서는 마우스 올림(title)이 없으므로 특이사항을 세 줄까지 보인다 */
  /* 한국어는 낱말 중간에서 줄을 바꾸지 않는다(keep-all), 띄어쓰기 없는 긴 글은 어디서든 끊는다 */
  .note-text {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
    line-clamp: 3;
    white-space: pre-wrap;
    word-break: keep-all;
    overflow-wrap: anywhere;
  }

  .contact-content {
    display: block;
    margin-top: 4px;
    font-size: 13px;
    line-height: 1.45;
    color: #c8c8c8;
    white-space: pre-wrap;
    word-break: keep-all;
    overflow-wrap: anywhere;
  }

  .cell-action {
    justify-content: stretch;
  }

  .log-btn {
    width: 100%;
    height: 40px;
    font-size: 14px;
  }
}
</style>
